import { supabase } from '../lib/supabase';

export interface SiteSection {
  id: number;
  section_key: string;
  title: string | null;
  subtitle: string | null;
  content_json: any;
  is_enabled: boolean;
  sort_order: number;
  updated_at: string;
}

// Obtener todas las secciones habilitadas (público)
export async function getPublicSections(): Promise<SiteSection[]> {
  const { data, error } = await supabase
    .from('site_sections')
    .select('*')
    .eq('is_enabled', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Obtener todas las secciones (admin)
export async function getAllSections(): Promise<SiteSection[]> {
  const { data, error } = await supabase
    .from('site_sections')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Obtener una sección por key
export async function getSectionByKey(key: string): Promise<SiteSection | null> {
  const { data, error } = await supabase
    .from('site_sections')
    .select('*')
    .eq('section_key', key)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Guardar/actualizar sección
export async function saveSection(section: Partial<SiteSection>): Promise<void> {
  const { section_key, title, subtitle, content_json, is_enabled, sort_order } = section;

  if (!section_key) throw new Error('section_key es requerido');

  const { error } = await supabase
    .from('site_sections')
    .upsert({
      section_key,
      title,
      subtitle,
      content_json,
      is_enabled,
      sort_order,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'section_key'
    });

  if (error) throw error;
}

// Actualizar orden de secciones
export async function updateSectionsOrder(sections: { section_key: string; sort_order: number }[]): Promise<void> {
  const updates = sections.map(s => 
    supabase
      .from('site_sections')
      .update({ sort_order: s.sort_order })
      .eq('section_key', s.section_key)
  );

  await Promise.all(updates);
}

// Restaurar sección a valores por defecto
export async function resetSection(sectionKey: string): Promise<void> {
  const defaults: Record<string, any> = {
    hero: {
      title: 'Hero Principal',
      subtitle: 'Carrusel de imágenes',
      content_json: {
        slides: [
          {
            title: 'Bodas de Ensueño',
            description: 'Capturamos cada momento mágico de tu día especial con elegancia y profesionalismo',
            image: 'https://readdy.ai/api/search-image?query=elegant%20romantic%20wedding%20ceremony%20beautiful%20bride%20and%20groom%20exchanging%20vows%20outdoor%20garden%20setting%20soft%20natural%20golden%20hour%20lighting%20dreamy%20atmosphere%20pastel%20cream%20beige%20rose%20gold%20tones%20simple%20clean%20background%20professional%20high%20end%20photography%20artistic%20composition&width=1920&height=1080&seq=hero-wedding-001&orientation=landscape',
            cta_text: 'Reservar Fecha',
            cta_url: '#agendar'
          }
        ]
      }
    },
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Ofrecemos servicios profesionales de fotografía y video',
      content_json: {
        items: []
      }
    },
    contact: {
      title: 'Información de Contacto',
      subtitle: '¿Tienes alguna pregunta?',
      content_json: {
        intro_text: 'Estamos aquí para ayudarte',
        phone: '',
        email: '',
        schedule: []
      }
    },
    footer: {
      title: 'Footer',
      subtitle: 'Pie de página',
      content_json: {
        copyright: '© 2024 Todos los derechos reservados',
        links: []
      }
    }
  };

  const defaultData = defaults[sectionKey];
  if (!defaultData) throw new Error('Sección no encontrada');

  const { error } = await supabase
    .from('site_sections')
    .update({
      ...defaultData,
      updated_at: new Date().toISOString()
    })
    .eq('section_key', sectionKey);

  if (error) throw error;
}
